import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Server, Search } from "lucide-react";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useHannoContent } from "@/data/hanno-content";
import { HannoMark } from "@/components/hanno/HannoMark";
import { CommandPalette, useCommandPaletteShortcut } from "@/components/hanno/CommandPalette";

const BSGOTECH_GREEN = "#0A9B5F";

export const Navbar = () => {
  const c = useHannoContent();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const [paletteOpen, setPaletteOpen] = React.useState(false);
  useCommandPaletteShortcut(setPaletteOpen);

  // Detect Mac for correct kbd hint
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform);

  const NAV_ITEMS = [
    { id: "home",              to: "/",                    label: c.nav.home },
    { id: "hx-energy",         to: "/hx-energy",           label: c.nav.hx_energy },
    { id: "hx-automotive",     to: "/hx-automotive",       label: c.nav.hx_automotive },
    { id: "hx-systems",        to: "/hx-systems",          label: c.nav.hx_systems },
    { id: "hx-infrastructure", to: "/hx-infrastructure",   label: c.nav.hx_infrastructure },
    { id: "hx-academy",        to: "/hx-academy",          label: c.nav.hx_academy },
    { id: "projects",          to: "/projects",            label: c.nav.projects },
    { id: "contact",           to: "/contact",             label: c.nav.contact },
  ];

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-hx-bg/90 backdrop-blur-2xl border-b border-white/[0.10]"
          : "bg-gradient-to-b from-hx-bg/60 to-transparent backdrop-blur-sm"
      }`}
      style={scrolled ? { boxShadow: "0 8px 40px -20px rgba(0,0,0,0.6)" } : undefined}
    >
      <div className={`max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between gap-6 transition-all duration-500 ${
        scrolled ? "h-14" : "h-16 lg:h-[70px]"
      }`}>
        {/* Brand — HannoNexus */}
        <Link to="/" className="group flex items-center gap-3 shrink-0" data-testid="brand-logo">
          <HannoMark
            size={38}
            className="relative shrink-0 transition-transform duration-500 group-hover:scale-[1.04]"
          />
          <div className="hidden sm:block leading-none whitespace-nowrap">
            <div className="font-display font-semibold tracking-tight text-[14px] text-hx-text">
              HannoNexus
            </div>
            <div className="mt-1 flex items-center gap-1.5 text-[8px] uppercase tracking-[0.24em] text-hx-muted/70">
              <Server size={7} strokeWidth={2.4} style={{ color: BSGOTECH_GREEN }} />
              <span>{c.brand.powered_by.replace("Powered by BSGOTECH", "Powered by ")}<span style={{ color: BSGOTECH_GREEN, fontWeight: 600 }}>BSGOTECH</span></span>
            </div>
          </div>
        </Link>

        {/* Nav */}
        <nav className="hidden xl:flex items-center gap-0.5 flex-1 justify-center" data-testid="primary-nav">
          {NAV_ITEMS.map((n) => (
            <NavLink
              key={n.id}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.id}`}
              className={({ isActive }) =>
                `relative px-3 py-2 whitespace-nowrap cursor-pointer inline-flex items-center gap-1 group ${isActive ? "is-active" : ""}`
              }
            >
              {({ isActive }) => (
                <>
                  <span className={`text-[10px] uppercase tracking-[0.16em] font-medium transition-colors ${
                    isActive ? "text-hx-neon" : "text-hx-muted/85 group-hover:text-hx-text"
                  }`}>
                    {n.label}
                  </span>
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute left-3 right-3 -bottom-0.5 h-[1.5px] bg-hx-neon rounded-full"
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="hidden lg:flex items-center gap-3 shrink-0">
          {/* Search trigger — command palette */}
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            data-testid="nav-search-trigger"
            aria-label={c.search?.trigger || "Search"}
            className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/[0.10] bg-white/[0.03] hover:bg-white/[0.03] hover:border-white/[0.10] transition-all"
          >
            <Search size={13} className="text-hx-muted group-hover:text-hx-neon transition-colors" />
            <span className="text-[10px] uppercase tracking-[0.16em] text-hx-muted group-hover:text-hx-text transition-colors">
              {c.search?.trigger || "Search"}
            </span>
            <kbd className="hidden xl:inline-flex items-center justify-center px-1.5 h-[16px] rounded border border-white/[0.10] bg-black/30 text-[9px] font-semibold text-hx-muted/80">
              {isMac ? "⌘K" : "Ctrl+K"}
            </kbd>
          </button>
          <LanguageSwitcher compact />
          <Link
            to="/contact"
            data-testid="nav-cta-meeting"
            className="group inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-hx-neon text-hx-bg font-semibold text-[10px] uppercase tracking-[0.14em] transition-all hover:bg-hx-neon-soft hover:translate-y-[-1px] whitespace-nowrap cursor-pointer"
            style={{ boxShadow: "0 0 14px -2px rgba(10,155,95,0.35)" }}
          >
            <span>{c.nav.cta_partnerships}</span>
          </Link>
        </div>

        {/* Mobile/Tablet search icon */}
        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          data-testid="nav-search-trigger-mobile"
          aria-label={c.search?.trigger || "Search"}
          className="lg:hidden ml-auto p-2 text-hx-muted hover:text-hx-neon transition-colors"
        >
          <Search size={18} />
        </button>

        {/* Mobile toggle */}
        <button
          className="xl:hidden p-2 text-hx-text"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="xl:hidden bg-hx-bg/95 backdrop-blur-2xl border-t border-white/[0.10] overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {NAV_ITEMS.map((n) => (
                <NavLink
                  key={n.id}
                  to={n.to}
                  end={n.to === "/"}
                  className={({ isActive }) =>
                    `text-[11px] uppercase tracking-[0.22em] py-3.5 border-b border-white/[0.10] transition-colors cursor-pointer ${
                      isActive ? "text-hx-neon" : "text-hx-muted hover:text-hx-neon"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                className="mt-5 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-hx-neon text-hx-bg font-medium text-[11px] uppercase tracking-[0.16em] cursor-pointer"
              >
                Strategic Partnerships
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Global Command Palette */}
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </motion.header>
  );
};
